import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase-init';
import SplashScreen from '../components/SplashScreen';

export const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading]         = useState(true);

  async function getUserProfileByUID(uid) {
    try {
      const userDocRef  = doc(db, 'users', uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        return { id: userDocSnap.id, ...userDocSnap.data() };
      }
      console.warn(`No user profile found for UID: ${uid}`);
      return null;
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      return null;
    }
  }

  function getRedirectPath(profile) {
    if (!profile || !profile.role) return '/';
    const role = profile.role.toLowerCase();
    switch (role) {
      case 'shop_worker':   return '/shop';
      case 'installer':     return '/installer';
      case 'manager':
      case 'admin':
      case 'installmanager':
      case 'sales':
      case 'officemanager': return '/';
      default:              return '/';
    }
  }

  async function signIn(email, password) {
    const result  = await signInWithEmailAndPassword(auth, email, password);
    const profile = await getUserProfileByUID(result.user.uid);
    if (profile) {
      setUserProfile(profile);
      return { user: result.user, profile, redirectPath: getRedirectPath(profile) };
    }
    return { user: result.user, profile: null, redirectPath: '/' };
  }

  async function signOut() {
    setUserProfile(null);
    return firebaseSignOut(auth);
  }

  // ── Auth state listener ───────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const profile = await getUserProfileByUID(user.uid);
        setUserProfile(profile ?? null);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    user:        userProfile,
    userProfile,
    signIn,
    signOut,
    getRedirectPath,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <SplashScreen /> : children}
    </AuthContext.Provider>
  );
}
